// Taking values out of a JSON response with a plain path like data.items[0].total.
// No expressions, no evaluation: a path is a list of keys and indexes.

import type { Extract, Point, StreamItem } from "./types";

export type PathPart = string | number;

export function parsePath(path: string): PathPart[] {
  const parts: PathPart[] = [];
  const src = path.trim().replace(/^\$\.?/, "");
  if (!src) return parts;
  const re = /([^.[\]]+)|\[(\d+)\]|\["([^"]*)"\]|\['([^']*)'\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(src))) {
    if (m[1] !== undefined) {
      const key = m[1];
      parts.push(/^\d+$/.test(key) ? Number(key) : key);
    } else if (m[2] !== undefined) parts.push(Number(m[2]));
    else if (m[3] !== undefined) parts.push(m[3]);
    else if (m[4] !== undefined) parts.push(m[4]);
  }
  return parts;
}

export function getPath(obj: unknown, path: string | undefined | null): unknown {
  if (path === undefined || path === null || path.trim() === "" || path.trim() === "$") return obj;
  let cur: unknown = obj;
  for (const part of parsePath(path)) {
    if (cur === null || cur === undefined) return undefined;
    if (typeof part === "number") {
      if (!Array.isArray(cur)) return undefined;
      cur = cur[part < 0 ? cur.length + part : part];
    } else {
      if (typeof cur !== "object") return undefined;
      cur = (cur as Record<string, unknown>)[part];
    }
  }
  return cur;
}

export function toNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const cleaned = v.replace(/[,\s$£€¥%]/g, "");
    if (cleaned === "" || !/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(cleaned)) return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** A list or table keeps this many rows of an answer, and says so when it had more. */
export const EXTRACT_MAX = 200;

export interface ExtractResult {
  ok: boolean;
  error?: string;
  /** a series: a number over time, oldest first, each point read from the answer, none made up */
  points?: Point[];
  value?: number | string | null;
  items?: StreamItem[];
  rows?: Record<string, unknown>[];
  columns?: string[];
  text?: string;
  json?: unknown;
  /** a list or table: how many the answer had, when it had more than were kept */
  of?: number;
}

/** "Shows the first 200 of 340.", for a list or table cut to what the board keeps. */
export function cutNote(r: Pick<ExtractResult, "rows" | "items" | "of">): string | undefined {
  const kept = r.rows?.length ?? r.items?.length;
  return kept !== undefined && r.of !== undefined && r.of > kept ? `Shows the first ${kept} of ${r.of}.` : undefined;
}

/** The object with the value put at a path, copying what it changes. */
export function setPath(obj: unknown, path: string | undefined | null, value: unknown): unknown {
  const at = (cur: unknown, parts: PathPart[]): unknown => {
    if (!parts.length) return value;
    const [head, ...rest] = parts;
    if (typeof head === "number") {
      const arr = Array.isArray(cur) ? [...cur] : [];
      arr[head] = at(arr[head], rest);
      return arr;
    }
    const o: Record<string, unknown> = cur && typeof cur === "object" && !Array.isArray(cur) ? { ...(cur as Record<string, unknown>) } : {};
    o[head] = at(o[head], rest);
    return o;
  };
  return at(obj, parsePath(path ?? ""));
}

/** A table cell: plain values as they are, anything else as short JSON. */
function cell(v: unknown): unknown {
  if (v === undefined) return null;
  return v !== null && typeof v === "object" ? JSON.stringify(v).slice(0, 120) : v;
}

function str(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return JSON.stringify(v).slice(0, 200);
}

export function runExtract(data: unknown, ex: Extract): ExtractResult {
  const target = getPath(data, ex.path);
  switch (ex.mode) {
    case "value": {
      if (target === undefined) return { ok: false, error: `Nothing at ${ex.path || "the top level"}` };
      const n = toNumber(target);
      if (n === null) {
        if (typeof target === "string") return { ok: true, value: target.slice(0, 200) };
        return { ok: false, error: `${ex.path || "The response"} is not a number` };
      }
      return { ok: true, value: ex.scale ? n * ex.scale : n };
    }
    case "list":
    case "table": {
      if (!Array.isArray(target)) return { ok: false, error: `${ex.path || "The response"} is not a list` };
      const arr = target.slice(0, EXTRACT_MAX);
      const of = target.length > arr.length ? target.length : undefined;
      if (ex.mode === "table") {
        // named fields by path: rows nested the way JSON:API nests them (attributes.total) read as columns
        const named = (ex.columns ?? []).filter((c) => c && typeof c.name === "string" && c.name && typeof c.path === "string").slice(0, 16);
        if (named.length) {
          const rows = arr.map((r) =>
            Object.fromEntries(
              named.map((c) => {
                const v = getPath(r, c.path);
                const n = c.scale ? toNumber(v) : null;
                // cents to dollars without float dust: 2149 cents is 21.49, not 21.490000000000002
                return [c.name, c.scale ? (n === null ? null : Number((n * c.scale).toPrecision(15))) : cell(v)];
              }),
            ),
          );
          return { ok: true, rows, columns: named.map((c) => c.name), ...(of ? { of } : {}) };
        }
        const rows = arr.map((r) => (r && typeof r === "object" && !Array.isArray(r) ? (r as Record<string, unknown>) : { value: r }));
        const columns: string[] = [];
        for (const r of rows.slice(0, 50)) for (const k of Object.keys(r)) if (!columns.includes(k) && columns.length < 12) columns.push(k);
        return { ok: true, rows: rows.map((r) => flattenRow(r, columns)), columns, ...(of ? { of } : {}) };
      }
      const items: StreamItem[] = arr.map((row, i) => {
        if (row === null || typeof row !== "object") return { label: str(row) ?? `#${i + 1}` };
        const label = ex.label ? str(getPath(row, ex.label)) : guessLabel(row as Record<string, unknown>);
        const rawValue = ex.value ? getPath(row, ex.value) : guessValue(row as Record<string, unknown>);
        const num = toNumber(rawValue);
        return {
          label: label ?? `#${i + 1}`,
          value: num !== null ? (ex.scale ? num * ex.scale : num) : str(rawValue),
          sub: ex.sub ? str(getPath(row, ex.sub)) : null,
          href: ex.href ? safeHref(str(getPath(row, ex.href))) : null,
        };
      });
      return { ok: true, items, ...(of ? { of } : {}) };
    }
    case "series": {
      if (!Array.isArray(target)) return { ok: false, error: `${ex.path || "The response"} is not a list` };
      const rows = target.slice(0, EXTRACT_MAX);
      const first = rows.find((r) => r && typeof r === "object" && !Array.isArray(r)) as Record<string, unknown> | undefined;
      const timeKey = ex.time ?? (first ? guessTime(first) : null);
      const valueKey = ex.value ?? (first ? guessSeriesValue(first, timeKey) : null);
      const points: Point[] = [];
      for (const r of rows) {
        // a row is [time, number] or an object with both; a row missing either is left out, never filled in
        const t = seriesTime(Array.isArray(r) ? r[0] : r && typeof r === "object" && timeKey ? getPath(r, timeKey) : undefined);
        const n = toNumber(Array.isArray(r) ? r[1] : r && typeof r === "object" && valueKey ? getPath(r, valueKey) : undefined);
        if (t !== null && n !== null) points.push({ t, v: ex.scale ? Number((n * ex.scale).toPrecision(15)) : n });
      }
      if (points.length < 2) return { ok: false, error: `No dates with numbers in ${ex.path || "the response"}${timeKey ? "" : ": name the field with each date"}` };
      points.sort((a, b) => a.t - b.t);
      return { ok: true, points, ...(target.length > rows.length ? { of: target.length } : {}) };
    }
    case "text": {
      if (target === undefined) return { ok: false, error: `Nothing at ${ex.path || "the top level"}` };
      return { ok: true, text: typeof target === "string" ? target : JSON.stringify(target, null, 2) };
    }
    case "json":
      if (target === undefined) return { ok: false, error: `Nothing at ${ex.path || "the top level"}` };
      return { ok: true, json: target };
  }
}

const TIME_KEYS = ["date", "day", "time", "timestamp", "t", "ts", "at", "period", "month", "week", "created_at", "createdAt"];

/** A date or a moment as a series names it: an ISO date or time, 20261013, a month like 2026-10, or seconds or ms since 1970. */
export function seriesTime(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v >= 1e9 && v < 1e11 ? Math.round(v * 1000) : v >= 1e11 && v < 1e14 ? Math.round(v) : null;
  if (typeof v !== "string") return null;
  const s = v.trim();
  let m = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  m = /^(\d{4})-(\d{2})$/.exec(s);
  if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, 1);
  if (/^\d+(\.\d+)?$/.test(s)) return seriesTime(Number(s));
  if (!/\d{4}/.test(s)) return null;
  const t = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(s) ? s + "T00:00:00Z" : s);
  return Number.isFinite(t) ? t : null;
}

function guessTime(row: Record<string, unknown>): string | null {
  for (const k of TIME_KEYS) if (seriesTime(row[k]) !== null) return k;
  for (const [k, v] of Object.entries(row)) if (typeof v === "string" && seriesTime(v) !== null) return k;
  return null;
}

function guessSeriesValue(row: Record<string, unknown>, timeKey: string | null): string | null {
  for (const k of VALUE_KEYS) if (k !== timeKey && toNumber(row[k]) !== null) return k;
  for (const [k, v] of Object.entries(row)) if (k !== timeKey && typeof v === "number") return k;
  return null;
}

/** Whether a list reads as a series: rows with a date and a number. */
export function looksLikeSeries(list: unknown): boolean {
  return Array.isArray(list) && list.length >= 2 && runExtract(list, { mode: "series", path: "" }).ok;
}

function flattenRow(r: Record<string, unknown>, columns: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const c of columns) {
    const v = r[c];
    out[c] = v !== null && typeof v === "object" ? JSON.stringify(v).slice(0, 120) : v;
  }
  return out;
}

const LABEL_KEYS = ["name", "title", "label", "page", "path", "url", "country", "key", "id", "email", "query"];
const VALUE_KEYS = ["value", "count", "total", "amount", "visitors", "views", "clicks", "revenue", "sessions", "users", "score"];

function guessLabel(row: Record<string, unknown>): string | null {
  for (const k of LABEL_KEYS) if (typeof row[k] === "string" || typeof row[k] === "number") return String(row[k]);
  for (const v of Object.values(row)) if (typeof v === "string") return v;
  return null;
}

function guessValue(row: Record<string, unknown>): unknown {
  for (const k of VALUE_KEYS) if (toNumber(row[k]) !== null) return row[k];
  for (const v of Object.values(row)) if (typeof v === "number") return v;
  return null;
}

export function safeHref(h: string | null | undefined): string | null {
  if (!h) return null;
  try {
    const u = new URL(h);
    return u.protocol === "https:" || u.protocol === "http:" || u.protocol === "mailto:" ? u.toString() : null;
  } catch {
    return null;
  }
}

// Every numeric leaf with its path, for the "point at the number" picker.
export interface Leaf {
  path: string;
  value: unknown;
  kind: "number" | "string" | "boolean" | "array" | "object" | "null";
  depth: number;
}

export function listLeaves(data: unknown, max = 400): Leaf[] {
  const out: Leaf[] = [];
  const walk = (v: unknown, path: string, depth: number) => {
    if (out.length >= max || depth > 8) return;
    const kind: Leaf["kind"] =
      v === null ? "null" : Array.isArray(v) ? "array" : typeof v === "object" ? "object" : (typeof v as "number" | "string" | "boolean");
    out.push({ path: path || "$", value: kind === "array" ? `[${(v as unknown[]).length}]` : kind === "object" ? "{…}" : v, kind, depth });
    if (kind === "array") (v as unknown[]).slice(0, 3).forEach((x, i) => walk(x, `${path}[${i}]`, depth + 1));
    else if (kind === "object")
      // as many fields as the server keeps in a sample (trimSample), so wide answers like GitHub's are all pickable
      for (const [k, x] of Object.entries(v as Record<string, unknown>).slice(0, 120)) {
        const safe = /^[A-Za-z_$][\w$]*$/.test(k) ? k : `["${k.replace(/"/g, "")}"]`;
        walk(x, path ? (safe.startsWith("[") ? path + safe : `${path}.${safe}`) : safe, depth + 1);
      }
  };
  walk(data, "", 0);
  return out;
}
